<script setup lang="ts">
  import { computed, nextTick, ref } from 'vue'
  import { onKeyStroke } from '@vueuse/core'
  import topicConfig from '@/config/deck/topics.json'
  import { useI18n } from 'vue-i18n'
  import { preferredLocale } from '@/plugins/i18n'
  import { useRoute, useRouter } from 'vue-router'
  import { storeToRefs } from 'pinia'
  import { useUserStore } from '@/store/user'
  import { useAiStore } from '@/store/ai'
  import { useDeckStore } from '@/store'
  import { pickEventName } from '@/lib/events'
  import {
    useFacetSuggestions,
    parseFacetInput,
    containsFacetToken,
    type FacetKey
  } from '@/composables/useFacetSuggestions'
  import {
    useProductMatcher,
    useTopicMatcher,
    useSkillMatcher,
    useIdentifierMatcher,
    useLanguageMatcher,
    useMapMatcher,
    useEventMatcher,
    type IdentifierMatch
  } from '@/composables/useOmniboxMatchers'
  import { buildDeckQuery, type DeckQueryInput } from '@/lib/deckQuery'
  import type { AfpClass } from '@/config/deck/classes'
  import OmniboxDropdown, {
    type OmniboxAction
  } from '@/components/OmniboxDropdown.vue'
  import {
    MagnifyingGlassIcon,
    MicrophoneIcon,
    XMarkIcon
  } from '@heroicons/vue/24/outline'

  const emit = defineEmits<{ (e: 'open-voice'): void }>()

  const { t } = useI18n({ useScope: 'global' })
  const router = useRouter()
  const route = useRoute()
  const { aiEnabled } = storeToRefs(useUserStore())
  const aiSkillsStore = useAiStore()
  const { getEventById, topLocalEvents } = useDeckStore()

  const placeholder = computed(() =>
    aiEnabled.value
      ? t('omnibox.placeholder-ai')
      : t('omnibox.placeholder-search')
  )

  // Nom d'une langue dans la locale UI courante (ex : "français", "English"),
  // lu depuis les fichiers de locale (clés `language-name.*`).
  function localeDisplayName(code: string): string {
    return t(`language-name.${code}`)
  }

  const preferredLocaleName = computed(() =>
    localeDisplayName(preferredLocale.value)
  )

  const search = ref('')
  const showDropdown = ref(false)
  const selectedIndex = ref(0)
  const showMobileSearch = ref(false)
  const desktopInputRef = ref<HTMLInputElement | null>(null)
  const mobileInputRef = ref<HTMLInputElement | null>(null)

  const { suggestions: facetSuggestions, loading: facetLoading } =
    useFacetSuggestions(search)
  const matchingProduct = useProductMatcher(search)
  const matchingTopics = useTopicMatcher(search)
  const matchingSkills = useSkillMatcher(search)
  const matchingIdentifier = useIdentifierMatcher(search)
  const matchingLanguage = useLanguageMatcher(search)
  const matchingMap = useMapMatcher(search)
  const matchingEvents = useEventMatcher(search)

  const topics = computed(() => topicConfig[preferredLocale.value])

  const shouldAutofocus = computed(() => route.name === 'deck')
  const suppressNextFocus = ref(shouldAutofocus.value)

  onKeyStroke(['k', 'K'], e => {
    if (!(e.ctrlKey || e.metaKey)) return
    e.preventDefault()
    if (window.matchMedia('(min-width: 640px)').matches) {
      desktopInputRef.value?.focus()
      desktopInputRef.value?.select()
    } else {
      openMobileSearch()
    }
  })

  function looksLikeQuestion(query: string): boolean {
    const trimmed = query.trim()
    if (containsFacetToken(trimmed)) return false
    if (trimmed.endsWith('?')) return true
    return trimmed.split(/\s+/).length >= 3
  }

  const aiFirst = computed(
    () => aiEnabled.value && looksLikeQuestion(search.value)
  )

  function reset() {
    search.value = ''
    showDropdown.value = false
    showMobileSearch.value = false
  }

  function goToDeck(input: DeckQueryInput) {
    router.replace({ name: 'deck-new', query: buildDeckQuery(input) })
    reset()
  }

  function goToAi(query: Record<string, string>) {
    router.push({ name: 'ai-new', query })
    reset()
  }

  function newEmptyColumn() {
    goToDeck({})
  }

  function searchInColumn() {
    if (!search.value.trim()) {
      newEmptyColumn()
      return
    }
    goToDeck({ q: search.value })
  }

  function askAi() {
    if (!search.value.trim()) return
    goToAi({ q: search.value })
  }

  function openTopic(value: string) {
    goToDeck({ topic: value, lang: preferredLocale.value })
  }

  function openProduct(cls: AfpClass, query: string) {
    goToDeck({ class: cls, q: query || undefined })
  }

  function openFacet(key: FacetKey, value: string) {
    goToDeck({ facet: { key, value } })
  }

  function openIdentifier(match: IdentifierMatch) {
    if (match.kind === 'document') {
      router.push({ name: 'document', params: { docId: match.id } })
      reset()
      return
    }
    goToDeck({ facet: { key: 'event', value: match.id } })
  }

  // Un `/skill` suivi d'un espace signale que l'utilisateur a fini de taper
  // l'id (le reste, éventuel, est l'argument) — distinct de `/skill` encore
  // en cours de frappe, qui doit continuer à proposer des suggestions.
  const SKILL_COMMAND_PATTERN = /^\/([a-z0-9_-]+)\s+([\s\S]*)$/i

  // Clic sur une suggestion : on affiche la commande dans le champ, on ne
  // l'envoie pas — l'utilisateur peut ajouter un argument puis valider par
  // Entrée (cf. action retournée par `skillCommandAction` une fois l'id complet).
  // Sur mobile, le tap déclenche un blur avant le click (fermeture différée
  // du dropdown, cf. onBlur) : on l'annule et on refocus pour garder le
  // clavier et la liste ouverts.
  async function fillSkillCommand(skillId: string) {
    clearTimeout(blurTimeout)
    search.value = `/${skillId} `
    showDropdown.value = true
    selectedIndex.value = 0
    await nextTick()
    desktopInputRef.value?.focus()
    mobileInputRef.value?.focus()
  }

  function sendSkillCommand(skillId: string, args: string) {
    goToAi({ q: args ? `/${skillId} ${args}` : `/${skillId}` })
  }

  function onInput() {
    showDropdown.value = true
    selectedIndex.value = 0
  }

  function onFocus() {
    if (suppressNextFocus.value) {
      suppressNextFocus.value = false
      return
    }
    showDropdown.value = true
    selectedIndex.value = 0
  }

  function onKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape') {
      showDropdown.value = false
      showMobileSearch.value = false
      return
    }
    if (!showDropdown.value) return
    const max = orderedActions.value.length - 1
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      selectedIndex.value = Math.min(selectedIndex.value + 1, max)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      selectedIndex.value = Math.max(selectedIndex.value - 1, 0)
    } else if (e.key === 'Enter') {
      e.preventDefault()
      orderedActions.value[selectedIndex.value]?.action()
    }
  }

  const identifierAction = computed<OmniboxAction | null>(() => {
    const match = matchingIdentifier.value
    if (!match) return null
    // Événement déjà connu localement (table events) : préférer son nom au
    // libellé générique, cohérent avec les suggestions de eventActions.
    if (match.kind === 'event') {
      const known = pickEventName(getEventById(match.id), [
        preferredLocale.value
      ])
      if (known) {
        return {
          key: `id:event:${match.id}`,
          type: 'event',
          label: known,
          sublabel: `#${match.id}`,
          action: () => openIdentifier(match)
        }
      }
    }
    return {
      key: `id:${match.kind}:${match.id}`,
      type: 'identifier',
      label: t(
        match.kind === 'document'
          ? 'omnibox.open-document'
          : 'omnibox.open-event',
        { id: match.id }
      ),
      sublabel:
        match.kind === 'document'
          ? `afpshortid:${match.id}`
          : `event:"afpevent:${match.id}"`,
      action: () => openIdentifier(match)
    }
  })

  const eventActions = computed<OmniboxAction[]>(() =>
    matchingEvents.value.map(ev => ({
      key: `event:${ev.id}`,
      type: 'event',
      label: ev.name,
      sublabel: `#${ev.id}`,
      action: () => openIdentifier({ kind: 'event', id: ev.id })
    }))
  )

  // "A la une" / "Top Stories" est toujours en première position dans
  // topics.json pour chaque locale — seule rubrique proposée à l'ouverture,
  // avant toute saisie (pas la peine de saturer le dropdown avec les 5).
  const defaultTopicActions = computed<OmniboxAction[]>(() =>
    topics.value.slice(0, 1).map(tp => ({
      key: `topic:${tp.value}`,
      type: 'topic',
      label: tp.label,
      sublabel: t('omnibox.open-topic'),
      action: () => openTopic(tp.value)
    }))
  )

  // Événements connus localement les plus référencés (par nombre de dépêches
  // déjà chargées) — proposés à l'ouverture, avant toute saisie.
  const defaultEventActions = computed<OmniboxAction[]>(() =>
    topLocalEvents([preferredLocale.value]).map(ev => ({
      key: `event:${ev.id}`,
      type: 'event',
      label: ev.name,
      sublabel: `#${ev.id}`,
      action: () => openIdentifier({ kind: 'event', id: ev.id })
    }))
  )

  const facetActions = computed<OmniboxAction[]>(() => {
    if (facetLoading.value && facetSuggestions.value.length === 0) {
      return [
        {
          key: 'facet-loading',
          type: 'facet',
          label: t('loading'),
          action: () => {}
        }
      ]
    }
    return facetSuggestions.value.map(s => ({
      key: `facet:${s.facet}:${s.value}`,
      type: 'facet',
      label: s.value,
      sublabel: `${s.facet} · ${s.count}`,
      action: () => openFacet(s.facet, s.value)
    }))
  })

  const skillActions = computed<OmniboxAction[]>(() =>
    matchingSkills.value.map(s => ({
      key: `skill:${s.id}`,
      type: 'skill',
      label: s.label ?? s.id,
      sublabel: `/${s.id}`,
      action: () => fillSkillCommand(s.id)
    }))
  )

  // Commande complète (`/skill` + espace, argument optionnel) : une seule
  // action "envoyer", au lieu de la liste de suggestions.
  const skillCommandAction = computed<OmniboxAction | null>(() => {
    const match = search.value.match(SKILL_COMMAND_PATTERN)
    if (!match) return null
    const [, id, args] = match
    const skill = aiSkillsStore.suggestions.find(s => s.id === id)
    if (!skill) return null
    const trimmedArgs = args.trim()
    return {
      key: `skill-command:${skill.id}`,
      type: 'skill',
      label: skill.label ?? skill.id,
      sublabel: trimmedArgs ? `/${skill.id} ${trimmedArgs}` : `/${skill.id}`,
      action: () => sendSkillCommand(skill.id, trimmedArgs)
    }
  })

  const productAction = computed<OmniboxAction | null>(() => {
    const match = matchingProduct.value
    if (!match) return null
    const productLabel = t(`products.${match.class}`)
    return {
      key: `product:${match.class}:${match.query}`,
      type: 'product',
      label: match.query
        ? t('omnibox.search-in-product', {
            query: match.query,
            product: productLabel
          })
        : t('omnibox.open-product', { product: productLabel }),
      action: () => openProduct(match.class, match.query)
    }
  })

  // Saisie d'un nom de langue → proposer une colonne « tout le contenu » dans
  // cette langue (réutilise le type 'empty', comme la suggestion par défaut).
  const languageAction = computed<OmniboxAction | null>(() => {
    const code = matchingLanguage.value
    if (!code) return null
    return {
      key: `language:${code}`,
      type: 'empty',
      label: t('omnibox.empty-column', { language: localeDisplayName(code) }),
      action: () => goToDeck({ lang: code })
    }
  })

  const mapAction = computed<OmniboxAction | null>(() => {
    const match = matchingMap.value
    if (!match) return null
    return {
      key: 'map',
      type: 'map',
      label: match.query
        ? t('omnibox.search-on-map', { query: match.query })
        : t('omnibox.open-map'),
      action: () => {
        router.push({
          name: 'map',
          query: match.query ? { q: match.query } : {}
        })
        reset()
      }
    }
  })

  const topicActions = computed<OmniboxAction[]>(() =>
    matchingTopics.value.map(tp => ({
      key: `topic:${tp.value}`,
      type: 'topic',
      label: tp.label,
      sublabel: t('omnibox.open-topic'),
      action: () => openTopic(tp.value)
    }))
  )

  const orderedActions = computed<OmniboxAction[]>(() => {
    const trimmed = search.value.trim()

    if (!trimmed) {
      return [
        {
          key: 'empty',
          type: 'empty',
          label: t('omnibox.empty-column', {
            language: preferredLocaleName.value
          }),
          action: newEmptyColumn
        },
        ...defaultTopicActions.value,
        ...defaultEventActions.value
      ]
    }

    if (parseFacetInput(trimmed)) return facetActions.value
    if (trimmed.startsWith('/'))
      return skillCommandAction.value
        ? [skillCommandAction.value]
        : skillActions.value

    const searchAction: OmniboxAction = {
      key: 'search',
      type: 'search',
      label: t('omnibox.search-column'),
      sublabel: search.value,
      action: searchInColumn
    }
    const aiAction: OmniboxAction = {
      key: 'ai',
      type: 'ai',
      label: t('omnibox.ask-ai'),
      sublabel: search.value,
      action: askAi
    }

    const head: OmniboxAction[] = [
      ...(mapAction.value ? [mapAction.value] : []),
      ...(languageAction.value ? [languageAction.value] : []),
      ...(identifierAction.value ? [identifierAction.value] : []),
      ...(productAction.value ? [productAction.value] : [])
    ]
    // Les rubriques et skills sont des correspondances floues : la recherche
    // littérale passe devant (sauf question, où l'IA reste en tête).
    if (!aiEnabled.value)
      return [
        ...head,
        searchAction,
        ...skillActions.value,
        ...topicActions.value,
        ...eventActions.value
      ]
    return aiFirst.value
      ? [
          ...head,
          aiAction,
          searchAction,
          ...skillActions.value,
          ...topicActions.value,
          ...eventActions.value
        ]
      : [
          ...head,
          searchAction,
          aiAction,
          ...skillActions.value,
          ...topicActions.value,
          ...eventActions.value
        ]
  })

  let blurTimeout: ReturnType<typeof setTimeout> | undefined

  function onBlur() {
    blurTimeout = setTimeout(() => {
      showDropdown.value = false
    }, 150)
  }

  async function openMobileSearch() {
    showMobileSearch.value = true
    await nextTick()
    mobileInputRef.value?.focus()
  }

  function closeMobileSearch() {
    showMobileSearch.value = false
    search.value = ''
    showDropdown.value = false
  }

  function openVoiceFromMobile() {
    closeMobileSearch()
    emit('open-voice')
  }

  router.afterEach(async to => {
    if (to.redirectedFrom?.name === 'deck-new') {
      await nextTick()
      document
        .getElementById('columns')
        ?.scrollTo({ left: 0, behavior: 'smooth' })
    }
    return true
  })
</script>

<template>
  <div>
    <!-- Omnibox for desktop -->
    <div
      class="hidden items-center sm:flex sm:flex-1"
      data-tour="omnibox"
    >
      <div class="relative w-full">
        <label class="input w-full">
          <input
            ref="desktopInputRef"
            type="search"
            :placeholder="placeholder"
            v-model="search"
            @input="onInput"
            @keydown="onKeydown"
            @blur="onBlur"
            @focus="onFocus"
            autocomplete="off"
            :autofocus="shouldAutofocus"
          />
          <kbd class="kbd kbd-sm">⌘K</kbd>
          <button
            v-if="aiEnabled"
            class="btn btn-ghost btn-xs btn-square -me-1"
            data-tour="voice-button"
            :aria-label="t('voice.title')"
            :title="t('voice.title')"
            @click="emit('open-voice')"
          >
            <MicrophoneIcon class="size-5" />
          </button>
        </label>
        <OmniboxDropdown
          v-if="showDropdown && orderedActions.length > 0"
          :items="orderedActions"
          v-model:selected-index="selectedIndex"
          variant="desktop"
          class="rounded-box bg-base-100 absolute top-full left-0 z-50 mt-1 shadow-lg"
        />
      </div>
    </div>

    <!-- Mobile: ouvre l'omnibox en modale -->
    <button
      class="btn btn-sm btn-square sm:hidden"
      data-tour="omnibox"
      :aria-label="placeholder"
      @click="openMobileSearch"
    >
      <MagnifyingGlassIcon class="size-5" />
    </button>

    <!-- Modale de recherche mobile (teleportée au body) -->
    <Teleport to="body">
      <Transition name="search-modal">
        <div
          v-if="showMobileSearch"
          class="fixed inset-0 z-50 flex items-start justify-center bg-black/30 px-4 pt-20 sm:hidden"
          @click="closeMobileSearch"
        >
          <div
            class="bg-base-100 w-full max-w-md shadow-xl"
            @click.stop
          >
            <div class="flex items-center gap-2 p-3">
              <label class="input flex-1">
                <input
                  ref="mobileInputRef"
                  type="search"
                  :placeholder="placeholder"
                  v-model="search"
                  @input="onInput"
                  @keydown="onKeydown"
                  @focus="onFocus"
                  autocomplete="off"
                />
                <button
                  v-if="aiEnabled"
                  class="btn btn-ghost btn-xs btn-square -me-1"
                  data-tour="voice-button"
                  :aria-label="t('voice.title')"
                  @click="openVoiceFromMobile"
                >
                  <MicrophoneIcon class="size-5" />
                </button>
              </label>
              <button
                class="btn btn-ghost btn-sm btn-square"
                @click="closeMobileSearch"
              >
                <XMarkIcon class="size-5" />
              </button>
            </div>
            <OmniboxDropdown
              v-if="showDropdown && orderedActions.length > 0"
              :items="orderedActions"
              v-model:selected-index="selectedIndex"
              variant="mobile"
              class="pb-2"
            />
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<style lang="css" scoped>
  .search-modal-enter-active,
  .search-modal-leave-active {
    transition: opacity 0.15s ease;
  }
  .search-modal-enter-from,
  .search-modal-leave-to {
    opacity: 0;
  }
</style>
